import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import { Layer, SecondaryLayer } from "../../interfaces/layer";
import { useStore } from "../../stores/store";
import TileLayer from "@arcgis/core/layers/TileLayer";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";

type LayerReturnType =
  | __esri.FeatureLayer
  | __esri.GraphicsLayer
  | __esri.MapImageLayer
  | __esri.VectorTileLayer;

export const createLayer = (layer: Layer): LayerReturnType => {
    let LayerClass: any = null;

    const layerTypeLookup = {
        feature: FeatureLayer,
        graphic: GraphicsLayer,
        tile: TileLayer,
        "map-image": MapImageLayer,
        osm: VectorTileLayer,
        "vector": VectorTileLayer,
    };

    if (!layer || !layer.type) {
        throw new Error("Layer type is required.");
    } else {
        LayerClass = layerTypeLookup[layer.type];
    }

    const newLayer = { ...layer };
    const state = useStore.getState();

    useStore.setState({
        layerVisibility: {
            ...state.layerVisibility,
            [layer.id]: layer.visible ?? false,
        },
    });

    useStore.setState({
        layerOpacity: {
            ...state.layerOpacity,
            [layer.id]: layer.opacity ?? 1,
        },
    });

    delete newLayer.type;

    const _layer = new LayerClass(newLayer);

    if (layer.type === "osm") {
        // console.log("created osm layer:", _layer);
    }

    return _layer;
};

// Function to build a companion legend layer
const buildCompanionLegendLayer = (displayLayer: SecondaryLayer): SecondaryLayer | null => {
    if (!displayLayer.companionLegendSource) {
        return null;
    }

    const src = displayLayer.companionLegendSource;
    const companionLayer: SecondaryLayer = {
        id: src.id ?? `${displayLayer.id}-legend-src`,
        title: src.title ?? `${displayLayer.title}`,
        url: src.url,
        type: src.type,
        visible: src.visible ?? false,
        opacity: src.opacity ?? 0,
        showInTOC: false,
        showInLegend: true,
        allowTransparencyChange: false,
        allowedWithLayers: ["*"],
        sublayers: src.sublayers,
        minScale: src.minScale,
        maxScale: src.maxScale
    };

    return companionLayer;
};

export const loadSecondaryLayers = async (
    layerConfigs: SecondaryLayer[] | undefined,
    map: __esri.Map,
) => {
    if (!layerConfigs || layerConfigs.length === 0) {
        return;
    }

    const layerPromises = layerConfigs.map(async (layerConfig) => {
        const created: __esri.Layer[] = [];

        const displayLayer = createLayer(layerConfig);
        created.push(displayLayer);

        const companion = buildCompanionLegendLayer(layerConfig);
        if (companion) {
            created.push(createLayer(companion) as __esri.Layer);
        }

        if (layerConfig.groupLayers?.length) {
            const groupLayerBuckets = await Promise.all(
                layerConfig.groupLayers.map(async (groupLayerConfig) => {
                    const groupCreated: __esri.Layer[] = [];

                    groupCreated.push(createLayer(groupLayerConfig) as __esri.Layer);

                    const groupCompanion = buildCompanionLegendLayer(groupLayerConfig);
                    if (groupCompanion) {
                        groupCreated.push(createLayer(groupCompanion) as __esri.Layer);
                    }

                    return groupCreated;
                })
            );

            created.push(...groupLayerBuckets.flat());
        }
        return created;
    });
    const createdLayersNest = await Promise.all(layerPromises);
    map.layers.addMany(createdLayersNest.flat());
};
